import React, { useEffect, useState } from "react";
import {
  Typography,
  Card,
  Select,
  Input,
  Tag,
  Space,
  Button,
  Row,
  Col,
  DatePicker,
  Tooltip,
  Avatar,
  Table,
  Modal,
  Collapse,
  Grid,
  message,
} from "antd";

import {
  SearchOutlined,
  FilterOutlined,
  UserOutlined,
  EyeOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  TagOutlined,
  FlagFilled,
  ReloadOutlined,
  CheckOutlined,
  MoreOutlined,
  MessageOutlined,
  CloseOutlined,
  DownloadOutlined,
} from "@ant-design/icons";

import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";

import {
  getAllCtaDataApi,
  getCtaFiltersApi,
  getCtaTopicsApi,
  getAgentsApi,
  assignAgentApi,
  exportCtaReportsApi,
} from "../../services/cta.service";

import { getFiltersApi } from "../../services/channels.service.js";

import dayjs from "dayjs";
import DataTablePagination from "../../components/common/dataTablePagination";
import CtaDetailsDialog from "./CtaDetailsDialog.Component";
import ResolveCtaDialog from "./ResolveCtaDialog.Component";
import { useNavigate, useSearchParams } from "react-router-dom";
import { logout } from "../../store/slices/profile.slice";
import prettifyString from "../../utils/prettifyString.js";
import { formatDateTime } from "../../utils/formatDateTime.js";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;
const { useBreakpoint } = Grid;

const CtaComponent = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { dateRangeValue } = useAppSelector((state) => state.dateRange);
  const [searchCta, setSearchCta] = useState(searchParams.get('search') || undefined);
  const [channelsList, setChannelsList] = useState();
  const [allFilters, setAllFilters] = useState();

  // Initialize state from URL parameters
  const [ctaType, setCtaType] = useState(searchParams.get('type') || undefined);
  const [channel, setChannel] = useState(searchParams.get('channels') || undefined);
  const [status, setStatus] = useState(searchParams.get('status') === "" ? undefined : searchParams.get('status') || 'open');
  const [assignedTo, setAssignedTo] = useState(searchParams.get('assigned_to') || undefined);
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('per_page')) || 10);
  const [pageNumber, setPageNumber] = useState(parseInt(searchParams.get('page')) || 1);

  // For date range: check URL first, then fall back to Redux state
  const urlStartDate = searchParams.get("start_date");
  const urlEndDate = searchParams.get("end_date");
  const [dates, setDates] = useState(() => {
    // If both dates exist and are not empty strings, use them
    if (urlStartDate !== null && urlEndDate !== null && urlStartDate !== "" && urlEndDate !== "") {
      return [urlStartDate, urlEndDate];
    }
    // If dates were explicitly cleared (empty strings), keep them cleared
    if (urlStartDate === "" && urlEndDate === "") {
      return [];
    }
    // Otherwise (no date params), use Redux state
    return [
      dayjs(dateRangeValue.start_date).hour(0).minute(0).second(0).format("YYYY-MM-DD HH:mm:ss"),
      dayjs(dateRangeValue.end_date).hour(23).minute(59).second(59).format("YYYY-MM-DD HH:mm:ss"),
    ];
  });

  // Initialize date picker values based on dates state
  const [dateRangeAntdValue, setDateRangeAntdValue] = useState(() => {
    // If both dates exist and are not empty strings, use them
    if (urlStartDate !== null && urlEndDate !== null && urlStartDate !== "" && urlEndDate !== "") {
      return [dayjs(urlStartDate), dayjs(urlEndDate)];
    }
    // If dates were explicitly cleared (empty strings), keep them cleared
    if (urlStartDate === "" && urlEndDate === "") {
      return null;
    }
    // Otherwise (no date params), use Redux state
    return [
      dayjs(dateRangeValue.start_date).hour(0).minute(0).second(0),
      dayjs(dateRangeValue.end_date).hour(23).minute(59).second(59),
    ];
  });

  const [ctaFilters, setCtaFilters] = useState();
  const [ctaTopics, setCtaTopics] = useState([]);
  const [users, setUsers] = useState([]);

  // CTA DATA
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [ctaData, setCtaData] = useState();
  const [ctaStatusDialogOpen, setCtaStatusDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Filters
  const [createdBy, setCreatedBy] = useState([]);
  const [resource, setResource] = useState([]);
  const [priority, setPriority] = useState([]);
  const [topic, setTopic] = useState([]);

  // Pagination
  const [totalDocuments, setTotalDocuments] = useState(0);

  const updatePageSize = (size) => {
    setPageSize(Number(size) || 10);
  };

  const updatePageNumber = (num) => {
    setPageNumber(Number(num) || 1);
  };

  // Dialog state
  const [isDetailsDialogVisible, setIsDetailsDialogVisible] = useState(false);
  const [selectedCta, setSelectedCta] = useState(null);

  // Handle Search filter
  const handleSearchFilter = (e) => {
    const value = e.target.value;
    setPageNumber(1);
    setSearchCta(value === "" ? undefined : value);
  };

  // Handle Date range filter
  const handleDateChange = (values) => {
    setPageNumber(1);
    setDateRangeAntdValue(values);
    if (values && values.length === 2) {
      // Preserve the time selected in the picker
      const stringDatesWithTime = values.map((date) =>
        date.format("YYYY-MM-DD HH:mm:ss")
      );
      setDates(stringDatesWithTime);
    } else {
      setDates([]);
    }
  };

  // Handle CTA select
  const handleCtaSelect = (record) => {
    setSelectedCta(record);
    setIsDetailsDialogVisible(true);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setIsDetailsDialogVisible(false);
    // setSelectedCta(null);
  };

  // handle jump to chat
  const handleJumpToChat = (record) => {
    const params = new URLSearchParams({
      search: record?.customer_email || record?.customer_id,
      start_date: "",
      end_date: "",
    });
    navigate(`/channels?${params.toString()}`);
  };

  const screens = useBreakpoint();
  const isMobile = screens.xs && !screens.md;

  const changeAgentAssignment = (ctaId, agentId) => {
    dispatch(
      assignAgentApi({
        params: {
          cta_id: ctaId,
          assign_to: agentId,
          mode: "assign",
        },
        finalCallback: () => {
          setRefreshTrigger((prev) => !prev);
        },
        successCallback: (response) => {},
        failureCallback: (errors) => {},
      })
    );
  };

  const buildCtaRequestParams = ({ includePagination = true, allData } = {}) => {
    const params = {};

    if (includePagination) {
      params.per_page = pageSize;
      params.page = pageNumber;
    }

    if (typeof allData === "boolean") {
      params.all_data = allData;
    }

    if (searchCta) params.search = searchCta;
    if (ctaType) params.type = ctaType;
    if (channel) params.channels = channel;
    if (Array.isArray(priority) && priority.length > 0) params.priority = priority;
    if (Array.isArray(createdBy) && createdBy.length > 0) params.created_by = createdBy;
    if (Array.isArray(resource) && resource.length > 0) params.resource = resource;
    if (Array.isArray(topic) && topic.length > 0) params.topic = topic;
    if (assignedTo) params.assigned_to = assignedTo;
    if (Array.isArray(dates) && dates.length === 2 && dates[0] && dates[1]) {
      params.start_date = dates[0];
      params.end_date = dates[1];
    }
    if (status !== undefined) {
      params.status = status;
    }

    return params;
  };

  const getFilenameFromContentDisposition = (contentDisposition) => {
    const fallbackName = `cta_reports_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`;

    if (!contentDisposition) {
      return fallbackName;
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1].replace(/"/g, ""));
    }

    const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return filenameMatch?.[1] || fallbackName;
  };

  const handleExportData = () => {
    setIsExporting(true);
    dispatch(
      exportCtaReportsApi({
        params: buildCtaRequestParams({ includePagination: true, allData: true }),
        finalCallback: () => setIsExporting(false),
        successCallback: (response) => {
          const filename = getFilenameFromContentDisposition(
            response?.headers?.["content-disposition"]
          );
          const blob = new Blob([response.data], {
            type:
              response?.headers?.["content-type"] ||
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          message.success("CTA data export started.");
        },
        failureCallback: (errors) => {
          console.error("Error exporting CTA data:", errors);
          const apiDetail = errors?.parsedDetail || errors?.response?.data?.detail;
          const userMessage = apiDetail || errors?.message || "Failed to export CTA data.";
          message.error(userMessage);
        },
      })
    );
  };

  // Define table columns
  const columns = [
    {
      title: "Date",
      key: "date",
      render: (text, record) => (
        <>
          {formatDateTime({
            date: record?.updated_at || record?.created_at,
            withTime: true,
          })}
        </>
      ),
    },
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (text, record) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {text || record?.extra_info?.customer_name || "N/A"}
        </div>
      ),
    },
    {
      title: "Channel",
      dataIndex: "channel",
      key: "type",
      render: (text) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {text[0].toUpperCase() + text.slice(1)}
        </div>
      ),
    },
    {
      title: "Details",
      key: "details",
      render: (text, record) => (
        <Button
          color="primary"
          variant="filled"
          icon={<EyeOutlined />}
          size="small"
          style={{ borderRadius: "6px" }}
          onClick={() => handleCtaSelect(record)}
        >
          View Details
        </Button>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (text) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {prettifyString(text)}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <Tag
            color={
              text === "open"
                ? "blue"
                : text === "resolved"
                ? "green"
                : text === "closed"
                ? "red"
                : "default"
            }
            style={{
              width: "70px",
              textAlign: "center",
            }}
          >
            {text[0].toUpperCase() + text.slice(1)}
          </Tag>
        </div>
      ),
    },
    {
      title: "Assigned To",
      dataIndex: "assigned_to_name",
      key: "assigned_to_name",
      render: (text, record) => {
        const handleChange = (value) => {
          const selectedUser = users.find((u) => u.key === value);

          Modal.confirm({
            title: "Change Assignment",
            content: (
              <>
                Are you sure you want to assign this task to{" "}
                <b>{selectedUser.name}</b>?
              </>
            ),
            okText: "Yes",
            cancelText: "No",
            onOk: () => {
              const userId = value;
              const ctaId = record.id;

              changeAgentAssignment(ctaId, userId);
              fetchAllCtaData();
            },
          });
        };

        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            {record.status === "open" ? (
              <Select
                style={{ width: 120 }}
                value={record.assigned_to}
                onChange={handleChange}
              >
                <Select.Option
                  key="placeholder"
                  value={null}
                  disabled
                  hidden
                  style={{ display: "none" }}
                >
                  -
                </Select.Option>
                {users.map((user) => (
                  <Select.Option key={user.key} value={user.key}>
                    {user.name}
                  </Select.Option>
                ))}
              </Select>
            ) : (
              <>{record.assigned_to_name}</>
            )}
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      render: (text, record) => (
        <div
          style={{
            display: "flex",
            gap: "8px",
          }}
        >
          <Tooltip title="View In Chat" placement="top">
            <Button
              icon={<MessageOutlined />}
              size="small"
              variant="filled"
              color="default"
              onClick={() => handleJumpToChat(record)}
            >
              View In Chat
            </Button>
          </Tooltip>
          {record.status === "open" && (
            <Tooltip title="Mark As Resolved" placement="top">
              <Button
                icon={<CheckOutlined />}
                size="small"
                variant="filled"
                color="danger"
                onClick={() => {
                  setSelectedCta(record);
                  setCtaStatusDialogOpen(true);
                }}
              >
                Resolve
              </Button>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  const filterContent = (
    <Row gutter={[16, 16]} align="top" style={{ width: "100%" }}>
      {/* Search Input */}
      <Col xs={24} sm={12} md={8} lg={4} xl={4}>
        <Text strong style={{ display: "block", marginBottom: 8 }}>
          Search
        </Text>
        <Input
          placeholder="Search..."
          prefix={<SearchOutlined style={{ color: "#1890ff" }} />}
          value={searchCta}
          onChange={handleSearchFilter}
          style={{
            borderRadius: "8px",
            backgroundColor: "#f9f9f9",
            border: "1px solid #e8e8e8",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          }}
          allowClear
        />
      </Col>

      {/* Date Range Picker */}
      <Col xs={24} sm={12} md={8} lg={8} xl={8}>
        <Text strong style={{ display: "block", marginBottom: 8 }}>
          Date Range
        </Text>
        <RangePicker
          value={dateRangeAntdValue}
          disabledDate={(d) => d && d.isAfter(dayjs(), "day")}
          onChange={handleDateChange}
          style={{
            width: "100%",
            borderRadius: "8px",
            backgroundColor: "#f9f9f9",
            border: "1px solid #e8e8e8",
          }}
          placeholder={["Start date", "End date"]}
          allowClear
          format="DD/MM/YYYY hh:mm A"
          showTime={{ use12Hours: true }}
          suffixIcon={<CalendarOutlined style={{ color: "#1890ff" }} />}
        />
      </Col>

      {/* Channel */}
      <Col xs={24} sm={12} md={8} lg={4} xl={4}>
        <Text strong style={{ display: "block", marginBottom: 8 }}>
          Channel
        </Text>
        <Select
          style={{
            width: "100%",
            backgroundColor: "#f9f9f9",
          }}
          placeholder="Channel"
          size="medium"
          suffixIcon={<FilterOutlined style={{ color: "#1890ff" }} />}
          onChange={(value) => {
            setPageNumber(1);
            setChannel(value);
          }}
          value={channel}
          allowClear
          showArrow
        >
          {allFilters?.channel?.map((item) => {
            return (
              <Option key={item} value={item}>
                {item}
              </Option>
            );
          })}
        </Select>
      </Col>

      {/* CTA Type */}
      <Col xs={24} sm={12} md={8} lg={4} xl={4}>
        <Text strong style={{ display: "block", marginBottom: 8 }}>
          CTA Type
        </Text>
        <Select
          style={{
            width: "100%",
            backgroundColor: "#f9f9f9",
          }}
          placeholder="CTA Type"
          size="medium"
          suffixIcon={<FilterOutlined style={{ color: "#1890ff" }} />}
          onChange={(value) => {
            setPageNumber(1);
            setCtaType(value);
          }}
          value={ctaType}
          allowClear
          showArrow
        >
          {allFilters?.type?.map((item) => {
            return (
              <Option key={item} value={item}>
                {prettifyString(item)}
              </Option>
            );
          })}
        </Select>
      </Col>

      {/* Status */}
      <Col xs={24} sm={12} md={8} lg={4} xl={4}>
        <Text strong style={{ display: "block", marginBottom: 8 }}>
          Status
        </Text>
        <Select
          style={{
            width: "100%",
            backgroundColor: "#f9f9f9",
          }}
          placeholder="Status"
          size="medium"
          suffixIcon={<FilterOutlined style={{ color: "#1890ff" }} />}
          onChange={(value) => {
            setPageNumber(1);
            setStatus(value); // Allow undefined when cleared
          }}
          value={status}
          allowClear
          showArrow
        >
          {allFilters?.status?.map((item) => {
            return (
              <Option key={item} value={item}>
                {prettifyString(item)}
              </Option>
            );
          })}
        </Select>
      </Col>

      {/* Assigned To */}
      <Col xs={24} sm={12} md={8} lg={4} xl={4}>
        <Text strong style={{ display: "block", marginBottom: 8 }}>
          Assigned To
        </Text>
        <Select
          style={{
            width: "100%",
            backgroundColor: "#f9f9f9",
          }}
          placeholder="Assigned To"
          size="medium"
          suffixIcon={<FilterOutlined style={{ color: "#1890ff" }} />}
          onChange={(value) => {
            setPageNumber(1);
            setAssignedTo(value);
          }}
          value={assignedTo}
          showArrow
          allowClear
        >
          {allFilters?.assigned_to?.map((item) => {
            return (
              <Option key={item?.id} value={item?.id}>
                {item?.name}
              </Option>
            );
          })}
        </Select>
      </Col>
    </Row>
  );

  const fetchCtaFilters = () => {
    dispatch(
      getCtaFiltersApi({
        params: {
          cta_type: ctaType,
        },
        finalCallback: () => {},
        successCallback: (response) => {
          setCtaFilters(response);
        },
        failureCallback: (errors) => {},
      })
    );
  };

  const fetchAllUsers = () => {
    dispatch(
      getAgentsApi({
        params: {},
        finalCallback: () => {},
        successCallback: (response) => {
          if (response && response.length > 0) {
            setUsers(response);
          }
        },
        failureCallback: (errors) => {},
      })
    );
  };

  const fetchAllCtaData = (checkEmptyPage = false) => {
    // Store current state before making API call
    const currentState = {
      pageNumber: parseInt(pageNumber),
      totalDocuments: totalDocuments || 0,
      hasData: (ctaData && ctaData.length > 0) || false,
      currentDataLength: ctaData ? ctaData.length : 0,
    };

    const params = buildCtaRequestParams();

    dispatch(
      getAllCtaDataApi({
        params,
        finalCallback: () => {},
        successCallback: (response) => {
          const responseData = response?.data || [];
          const totalDocs = response?.meta?.total || 0;
          const currentPageFromResponse = response?.meta?.page || 1;

          // Enhanced empty page detection
          if (checkEmptyPage && responseData.length === 0) {
            // Calculate if we should navigate to previous page
            const shouldNavigateToPrevious =
              currentPageFromResponse > 1 && // Not on first page
              (currentState.hasData || // Had data before
                (currentState.totalDocuments > 0 &&
                  totalDocs < currentState.totalDocuments)); // Total count decreased

            if (shouldNavigateToPrevious) {
              // Calculate target page
              const maxPossiblePages = Math.ceil(
                totalDocs / parseInt(pageSize)
              );
              const targetPage = Math.min(
                Math.max(1, maxPossiblePages),
                currentPageFromResponse - 1
              );

              console.log("Navigating to previous page:", targetPage);

              // Set the target page
              updatePageNumber(targetPage);

              // Fetch data for the target page with a small delay to ensure state is updated
              setTimeout(() => {
                const targetParams = {
                  ...params,
                  page: targetPage,
                };

                // Only include status if it's defined
                if (status !== undefined) {
                  targetParams.status = status;
                }

                dispatch(
                  getAllCtaDataApi({
                    params: targetParams,
                    finalCallback: () => {},
                    successCallback: (newResponse) => {
                      const newResponseData = newResponse?.data || [];
                      const newTotalDocs = newResponse?.meta?.total || 0;
                      const newCurrentPage = newResponse?.meta?.page || 1;

                      setCtaData(newResponseData);
                      setTotalDocuments(newTotalDocs);
                      updatePageNumber(newCurrentPage);
                    },
                    failureCallback: (errors) => {
                      console.error("Error fetching target page data:", errors);
                      // Fallback: set data anyway to avoid infinite loading
                      setCtaData(responseData);
                      setTotalDocuments(totalDocs);
                      updatePageNumber(currentPageFromResponse);
                    },
                  })
                );
              }, 100);
              return;
            }
          }

          // Normal case: just set the data
          setCtaData(responseData);
          setTotalDocuments(totalDocs);
          const normalizedPage = Number(currentPageFromResponse) || 1;
          if (normalizedPage !== pageNumber) {
            updatePageNumber(normalizedPage);
          }
        },
        failureCallback: (errors) => {
          console.error("Error fetching CTA data:", errors);
        },
      })
    );
  };

  const fetchCtaTopics = async () => {
    dispatch(
      getCtaTopicsApi({
        params: {
          type: ctaType,
        },
        finalCallback: () => {},
        successCallback: (response) => {
          if (response && response.length > 0) {
            setCtaTopics(response);
          }
        },
        failureCallback: (errors) => {},
      })
    );
  };

  const fetchFilters = () => {
    dispatch(
      getFiltersApi({
        params: { filter_route: "CTA" },
        finalCallback: () => {},
        successCallback: (response) => {
          console.log("RESPONSE -> ", response);
          setAllFilters(response);
        },
        failureCallback: (errors) => {},
      })
    );
  };

  useEffect(() => {
    fetchCtaTopics();
    fetchAllUsers();
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchAllCtaData();
  }, [
    searchCta,
    ctaType,
    channel,
    dates,
    createdBy,
    resource,
    priority,
    status,
    pageNumber,
    pageSize,
    topic,
    refreshTrigger,
    assignedTo,
  ]);

  // Update URL parameters when filter state changes
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (searchCta) params.set("search", searchCta);
    if (ctaType) params.set("type", ctaType);
    if (channel) params.set("channels", channel);
    if (status) params.set("status", status);
    else if (status === undefined) params.set("status", ""); // Track cleared status
    if (assignedTo) params.set("assigned_to", assignedTo);
    if (pageNumber !== 1) params.set("page", pageNumber.toString());
    if (pageSize !== 10) params.set("per_page", pageSize.toString());
    
    // Handle date range: set params if dates exist, set empty if explicitly cleared
    if (dates && dates.length === 2) {
      params.set("start_date", dates[0]);
      params.set("end_date", dates[1]);
    } else if (dates && dates.length === 0) {
      // Dates were explicitly cleared - track this with empty values
      params.set("start_date", "");
      params.set("end_date", "");
    }
    
    const newSearch = params.toString();
    const currentSearch = searchParams.toString();
    
    if (newSearch !== currentSearch) {
      setSearchParams(params, { replace: true });
    }
  }, [searchCta, ctaType, channel, status, assignedTo, pageNumber, pageSize, dates, setSearchParams, searchParams]);

  return (
    <>
      {/* Search and Filter Header */}
      <Card
        className="dashboard-header"
        style={{
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            borderBottom: "1px solid #f0f0f0",
            marginBottom: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: "8px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Title
              level={4}
              style={{
                color: "#333",
                fontWeight: 700,
                margin: 0,
              }}
            >
              Call To Action
            </Title>
            <Tooltip title={`${totalDocuments} CTAs found for selected filters`}>
              <Tag
                color="blue"
                style={{
                  margin: 0,
                  borderRadius: 999,
                  padding: "2px 10px",
                  fontWeight: 600,
                }}
              >
                {totalDocuments} CTAs Found
              </Tag>
            </Tooltip>
          </div>

          <Space wrap>
            <Tooltip title="Refresh Data" placement="top">
              <Button
                type="text"
                color="primary"
                variant="outlined"
                icon={<ReloadOutlined />}
                size="medium"
                onClick={() => setRefreshTrigger((prev) => !prev)}
              >
                Refresh
              </Button>
            </Tooltip>
            <Tooltip title="Export selected CTA data" placement="top">
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                size="medium"
                loading={isExporting}
                onClick={handleExportData}
              >
                Export Data
              </Button>
            </Tooltip>
          </Space>
        </div>

        {isMobile ? (
          <Collapse
            defaultActiveKey={["1"]}
            style={{
              marginBottom: 16,
              backgroundColor: "#fff",
              borderRadius: "8px",
              border: "1px solid #e8e8e8",
            }}
            expandIconPosition="end"
          >
            <Panel
              header={
                <Text strong style={{ fontSize: 16 }}>
                  Filters
                </Text>
              }
              key="1"
            >
              {filterContent}
            </Panel>
          </Collapse>
        ) : (
          <div style={{ marginBottom: 16 }}>{filterContent}</div>
        )}
      </Card>

      <div
        style={{
          background: "#fff",
          borderRadius: "8px",
          paddingBottom: "16px",
        }}
      >
        <div
          style={{
            overflowX: "auto",
            width: "100%",
            whiteSpace: "nowrap",
          }}
        >
          <Table
            dataSource={ctaData}
            columns={columns}
            rowKey={(item) => item._id}
            pagination={false}
            bordered
            rowClassName={(record) =>
              record?.id === selectedCta?.id ? "table-row-selected" : ""
            }
            footer={false}
          />
        </div>
        <DataTablePagination
          totalDocuments={totalDocuments}
          pageSize={pageSize}
          setPageSize={updatePageSize}
          setPageNumber={updatePageNumber}
          currentPage={pageNumber}
          showTotal={(total) => `Total ${total} CTAs`}
        />
      </div>

      <CtaDetailsDialog
        visible={isDetailsDialogVisible}
        ctaData={selectedCta}
        onClose={handleDialogClose}
        setCtaStatusDialogOpen={setCtaStatusDialogOpen}
      />
      <ResolveCtaDialog
        visible={ctaStatusDialogOpen}
        ctaData={selectedCta}
        onClose={() => setCtaStatusDialogOpen(false)}
        onSuccess={() => {
          setCtaStatusDialogOpen(false);
          setIsDetailsDialogVisible(false);
          fetchAllCtaData(true); // Pass true to check for empty page
        }}
      />
    </>
  );
};

export default CtaComponent;
